import { isDevelopment } from '../universal';
import { AppSetting } from 'dependencies/appsettings';
import { notifyError } from 'app/infrastructure/notifications';

/* tslint:disable */

/**
 * IMPORTANT NOTE: Logging is considered a side-effect from a functional perspective.
 *
 * Logging should therefore never be performed from withing a reducer or an execution path
 * originating from within a reducer
 */

/**
 * Log detailed info only visible in development or when appsetting ClientDebugLog is true.
 * This should be a app setting loaded from server.
 *
 * console.debug wrapper with fallback to console.log
 * @export
 * @param {string} message
 * @param {...any[]} args
 */
export function debug(message: string, ...args: any[]) {

  if (
    !isDevelopment &&
    !AppSetting.ClientDebugLog) {

    return;
  }

  if (typeof (console) === "undefined") return;
  const hasDebug = console && typeof console.debug !== "undefined";
  hasDebug ? console.debug(message, ...args) : console.log(message, ...args);

}

/**
 * Note: non-pure code with side effects
 *
 * Use for centralized logging about application state and execution
 *
 * @export
 * @param {string} message
 * @param {...any[]} args
 */
export function log(message: string, ...args: any[]) {
  console.log(message, ...args);
}

function deconstruct(errorOrMessage: any, ...args: any[]): string[]{

  if (typeof errorOrMessage === 'string'){
    return [errorOrMessage, ...args];
  }

  const errorStrategies = [

    function httpErrorWithMessages(err: any): string[]{
      if (err && err.response){
        if (Array.isArray(err.userMessages)){
          return [err.message, err.userMessages.join('\n')];
        }
        return [err.message, err.response.statustext];
      }
      return undefined;
    },

    function normalError(err: any): string[]{
      return [err.message];
    }

  ];

  return errorStrategies.map(strategy => strategy(errorOrMessage)).filter(defined=>defined)[0];

}

/**
 * NOTE: non-pure with side-effects. Do not use from reducer. Use `log()` instead.
 *
 * Used for centralized handling of *unexpected* errors in a try-catch context.
 *
 * Forwards the error to logging mechanisms
 *
 * @export
 * @param {string} message
 * @param {...any[]} args
 */
export function error(messageOrError: string, ...args: any[]) {

  if (console && console.error) {
    console.error(messageOrError, ...args);
  } else if (console) {
    console.log(messageOrError, ...args);
  }
  const [message, ...details] = deconstruct(messageOrError, ...args);
  notifyError( message, details.join(' ') );
}

/**
 * DO NOT USE - unless you know what you are doing
 *
 * Fatal errors only occurs on application start, or in
 * selected exceptional states
 *
 * > This message cannot be translated as the app may not have the app itself mounted
 */
export function fatal(errorMessage: string, ...args: any[]){

  const details: string = args.length ? args[0] : '';

  notifyError('Synergi life encountered a fatal error', `${errorMessage} ${details}` );

  // cannot use error() here as it will overflow
  console.error(errorMessage, ...args);

}

/**
 * Use invariant() to assert state which your program assumes to be true.
 * The invariant message is stripped in production and replaced with a generic message.
 *
 * @param {boolean} condition The condition expected to be *true*. If false then the error will be thrown.
 */
const DEFAULT_PROD_MESSAGE = 'Error (invariant violation)';
export function invariant(condition: boolean, message: string, ...rest: any[]) {
  if (!condition) {
    if (!isDevelopment) {
      throw new Error(DEFAULT_PROD_MESSAGE);
    }
    const err = new Error(`${message} \n ${rest.join('\n')}`);
    err.name = 'Invariant violation';
    throw err;
  }
}
/* tslint:enable */
